import { alpha, styled } from '@mui/material/styles';
import { Box, Button, IconButton, ListItemButton, Paper, TextField } from '@mui/material';

export const AgentRoot = styled('div')(({ theme }) => ({
  height: 'calc(100vh - 64px)',
  minHeight: 520,
  display: 'grid',
  gridTemplateColumns: '300px minmax(0, 1fr)',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    height: 'calc(100vh - 56px)',
    minHeight: 0,
    gridTemplateColumns: '1fr',
  },
}));

export const AgentSidebar = styled('aside')(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    width: 320,
    maxWidth: '85vw',
    height: '100%',
  },
}));

export const AgentSidebarHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const AgentConversationList = styled('div')({
  overflowY: 'auto',
  minHeight: 0,
  flex: 1,
});

export const AgentConversationItem = styled(ListItemButton)(({ theme, selected }) => ({
  alignItems: 'center',
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  ...(selected
    ? {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
      }
    : {}),
}));

export const AgentConversationActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

export const AgentSidebarControls = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'grid',
  gap: theme.spacing(1),
}));

export const AgentBulkToolbar = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
}));

export const AgentEmptyState = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 2),
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

export const AgentMain = styled('main')({
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const AgentMainHeader = styled('div')(({ theme }) => ({
  minHeight: 64,
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
}));

export const AgentMessages = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const AgentMessageRow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => ({
  display: 'flex',
  justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
}));

export const AgentMessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'role',
})(({ theme, role }) => ({
  maxWidth: role === 'user' ? '78%' : '88%',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  overflowWrap: 'anywhere',
  ...(role === 'user'
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }
    : {
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }),
  '& p:first-of-type': {
    marginTop: 0,
  },
  '& p:last-child': {
    marginBottom: 0,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

export const AgentComposer = styled('form')(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  gap: theme.spacing(1),
  alignItems: 'flex-end',
  backgroundColor: theme.palette.background.paper,
}));

export const AgentTextField = styled(TextField)({
  '& textarea': {
    resize: 'none',
  },
});

export const AgentAttachButton = styled(IconButton)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

export const AgentSendButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(6),
  height: theme.spacing(5),
  marginBottom: theme.spacing(0.5),
}));

export const AgentAttachmentTray = styled(Box)(({ theme }) => ({
  gridColumn: '2 / 4',
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
}));

export const AgentResultList = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}));

export const AgentResultItem = styled(ListItemButton)(({ theme }) => ({
  alignItems: 'flex-start',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}));
